import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiFileText, FiChevronDown, FiChevronRight, FiLock, FiUpload, FiEye, FiRefreshCw } from 'react-icons/fi';
import api, { openLicencaPdf } from '../services/api';
import { useAuth } from '../context/AuthContext';
import ACLManager from '../components/ACLManager';
import TableFilters, { SortableTh } from '../components/TableFilters';
import './Licencas.css';

const Licencas = () => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [licencas, setLicencas] = useState([]);
  const [total, setTotal] = useState(0);
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [condicionantes, setCondicionantes] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showACLModal, setShowACLModal] = useState(false);
  const [selectedCondicionante, setSelectedCondicionante] = useState(null);
  const [expandedLicenca, setExpandedLicenca] = useState(null);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [filterEmp, setFilterEmp] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [filterOrgao, setFilterOrgao] = useState('');
  const [filterValidadeDe, setFilterValidadeDe] = useState('');
  const [filterValidadeAte, setFilterValidadeAte] = useState('');
  const [filterEmissaoDe, setFilterEmissaoDe] = useState('');
  const [filterEmissaoAte, setFilterEmissaoAte] = useState('');
  const [filtrosOptions, setFiltrosOptions] = useState({ tipos: [], orgao_emissores: [] });
  const [sort, setSort] = useState('data_vencimento');
  const [order, setOrder] = useState('asc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [formData, setFormData] = useState({
    empreendimento_id: '',
    tipo: '',
    numero: '',
    orgao_emissor: '',
    data_emissao: '',
    data_vencimento: '',
    pdf: null
  });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadPdf, setUploadPdf] = useState(null);
  const [reprocessingId, setReprocessingId] = useState(null);

  const carregarEmpreendimentos = useCallback(async () => {
    try {
      const empRes = await api.get('/api/empreendimentos');
      const data = empRes.data;
      setEmpreendimentos(Array.isArray(data) ? data : (data?.data || []));
    } catch (err) {
      console.error('Erro ao carregar empreendimentos:', err);
      setEmpreendimentos([]);
    }
  }, []);

  const carregarFiltrosOptions = useCallback(async () => {
    try {
      const res = await api.get('/api/licencas/filtros');
      setFiltrosOptions(res.data || { tipos: [], orgao_emissores: [] });
    } catch {
      setFiltrosOptions({ tipos: [], orgao_emissores: [] });
    }
  }, []);

  const carregarLicencas = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.set('q', search);
      if (filterEmp) params.set('empreendimento_id', filterEmp);
      if (filterTipo) params.set('tipo', filterTipo);
      if (filterOrgao) params.set('orgao_emissor', filterOrgao);
      if (filterValidadeDe) params.set('validade_de', filterValidadeDe);
      if (filterValidadeAte) params.set('validade_ate', filterValidadeAte);
      if (filterEmissaoDe) params.set('emissao_de', filterEmissaoDe);
      if (filterEmissaoAte) params.set('emissao_ate', filterEmissaoAte);
      params.set('sort', sort);
      params.set('order', order);
      params.set('page', page);
      params.set('limit', limit);
      const licencasRes = await api.get(`/api/licencas?${params}`);
      const data = licencasRes.data;
      if (data && typeof data === 'object' && 'data' in data) {
        setLicencas(data.data);
        setTotal(data.total || 0);
      } else {
        setLicencas(Array.isArray(data) ? data : []);
        setTotal(Array.isArray(data) ? data.length : 0);
      }
    } catch (error) {
      console.error('Erro ao carregar licenças:', error);
      alert('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [search, filterEmp, filterTipo, filterOrgao, filterValidadeDe, filterValidadeAte, filterEmissaoDe, filterEmissaoAte, sort, order, page, limit]);

  useEffect(() => {
    carregarEmpreendimentos();
  }, [carregarEmpreendimentos]);

  useEffect(() => {
    carregarFiltrosOptions();
  }, [carregarFiltrosOptions]);

  useEffect(() => {
    carregarLicencas();
  }, [carregarLicencas]);

  // Quando vier de um empreendimento com ?empreendimentoId=..., já abre o modal de upload
  useEffect(() => {
    const empFromUrl = searchParams.get('empreendimentoId');
    if (empFromUrl) {
      setShowUploadModal(true);
    }
  }, [searchParams]);

  const carregarDados = () => {
    carregarLicencas();
  };

  const handleSort = (col) => {
    setSort(col);
    setOrder((o) => (sort === col && o === 'asc' ? 'desc' : 'asc'));
    setPage(1);
  };

  const carregarCondicionantes = async (licencaId) => {
    try {
      const response = await api.get(`/api/condicionantes?licenca_id=${licencaId}`);
      setCondicionantes(prev => ({ ...prev, [licencaId]: response.data }));
    } catch (error) {
      console.error('Erro ao carregar condicionantes:', error);
    }
  };

  const toggleExpandLicenca = (licencaId) => {
    if (expandedLicenca === licencaId) {
      setExpandedLicenca(null);
    } else {
      setExpandedLicenca(licencaId);
      if (!condicionantes[licencaId]) {
        carregarCondicionantes(licencaId);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('empreendimento_id', formData.empreendimento_id);
      formDataToSend.append('tipo', formData.tipo);
      formDataToSend.append('numero', formData.numero);
      formDataToSend.append('orgao_emissor', formData.orgao_emissor);
      formDataToSend.append('data_emissao', formData.data_emissao);
      formDataToSend.append('data_vencimento', formData.data_vencimento);
      
      if (formData.pdf) {
        formDataToSend.append('pdf', formData.pdf);
      }

      if (editing) {
        // Atualizar licença
        await api.put(`/api/licencas/${editing}`, {
          tipo: formData.tipo,
          numero: formData.numero,
          orgao_emissor: formData.orgao_emissor,
          data_emissao: formData.data_emissao,
          data_vencimento: formData.data_vencimento
        });
      } else {
        // Criar licença
        await api.post('/api/licencas', formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      
      setShowModal(false);
      setEditing(null);
      setFormData({
        empreendimento_id: '',
        tipo: '',
        numero: '',
        orgao_emissor: '',
        data_emissao: '',
        data_vencimento: '',
        pdf: null
      });
      carregarDados();
    } catch (error) {
      console.error('Erro ao salvar licença:', error);
      alert(error.response?.data?.error || 'Erro ao salvar licença');
    }
  };

  const handleEdit = (lic) => {
    setEditing(lic.id);
    setFormData({
      empreendimento_id: lic.empreendimento_id,
      tipo: lic.tipo || '',
      numero: lic.numero || '',
      orgao_emissor: lic.orgao_emissor || '',
      data_emissao: lic.data_emissao || '',
      data_vencimento: lic.data_vencimento || '',
      pdf: null
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta licença?')) {
      return;
    }
    try {
      await api.delete(`/api/licencas/${id}`);
      carregarDados();
    } catch (error) {
      console.error('Erro ao excluir licença:', error);
      alert('Erro ao excluir licença');
    }
  };

  const handleReprocessar = async (licenca) => {
    if (!window.confirm('Reprocessar esta licença com IA usando o PDF já salvo?')) return;
    try {
      setReprocessingId(licenca.id);
      const res = await api.post(`/api/licencas/${licenca.id}/reprocessar`);
      alert(res.data?.message || 'Reprocessamento da licença iniciado.');
      navigate(res.data?.redirect || `/licencas/${licenca.id}/validar`);
    } catch (error) {
      console.error('Erro ao reprocessar licença:', error);
      alert(error.response?.data?.error || 'Não foi possível reprocessar a licença.');
    } finally {
      setReprocessingId(null);
    }
  };

  useEffect(() => {
    if (!uploading) return;
    setUploadProgress(0);
    const start = Date.now();
    const maxProgress = 90;
    const duration = 60000; // 60s para chegar a 90%
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const progress = Math.min(maxProgress, Math.floor((elapsed / duration) * maxProgress));
      setUploadProgress(progress);
    }, 500);
    return () => clearInterval(interval);
  }, [uploading]);

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!uploadPdf) {
      alert('Selecione um arquivo PDF.');
      return;
    }
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('pdf', uploadPdf);
      const res = await api.post('/api/licencas/upload-extrair', formData);
      setUploadProgress(100);
      const redirect = res.data?.redirect || `/licencas/${res.data?.licenca?.id}/validar`;
      setShowUploadModal(false);
      setUploadPdf(null);
      await new Promise((r) => setTimeout(r, 400));
      navigate(redirect);
    } catch (error) {
      console.error('Erro ao processar PDF:', error);
      alert(error.response?.data?.error || 'Erro ao processar PDF. Verifique se o arquivo é válido.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      ok: 'badge-success',
      vencendo: 'badge-warning',
      vencida: 'badge-danger',
      ativa: 'badge-success',
      renovada: 'badge-info'
    };
    return badges[status] || 'badge-secondary';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const getStatusLabel = (status) => {
    const labels = {
      ok: 'OK',
      vencendo: 'Vencendo',
      vencida: 'Vencida',
      ativa: 'Ativa',
      renovada: 'Renovada'
    };
    return labels[status] || status;
  };

  if (loading) {
    return <div className="loading">Carregando...</div>;
  }

  return (
    <div className="licencas-page">
      <div className="page-header">
        <h1 className="page-title">
          <FiFileText className="page-title-icon" />
          Licenças
        </h1>
        {hasPermission('license', 'create') && (
          <div className="page-header-buttons">
            <button className="btn btn-primary" onClick={() => { setShowUploadModal(true); setUploadPdf(null); }}>
              <FiUpload /> Upload PDF
            </button>
          </div>
        )}
      </div>

      <TableFilters
        search={search}
        onSearchChange={setSearch}
        filterRows={[
          ['search', 'empreendimento'],
          ['tipo', 'orgao_emissor'],
          ['validade_de', 'validade_ate', 'emissao_de', 'emissao_ate']
        ]}
        filters={[
          {
            key: 'empreendimento',
            label: 'Empreendimento',
            value: filterEmp,
            onChange: (v) => { setFilterEmp(v); setPage(1); },
            options: [
              { value: '', label: 'Todos' },
              ...(empreendimentos.map((e) => ({ value: e.id, label: e.nome })))
            ]
          },
          {
            key: 'tipo',
            label: 'Tipo',
            value: filterTipo,
            onChange: (v) => { setFilterTipo(v); setPage(1); },
            options: [
              { value: '', label: 'Todos' },
              ...(filtrosOptions.tipos.map((t) => ({ value: t, label: t })))
            ]
          },
          {
            key: 'orgao_emissor',
            label: 'Órgão emissor',
            value: filterOrgao,
            onChange: (v) => { setFilterOrgao(v); setPage(1); },
            options: [
              { value: '', label: 'Todos' },
              ...(filtrosOptions.orgao_emissores.map((o) => ({ value: o, label: o })))
            ]
          },
          {
            key: 'validade_de',
            label: 'Validade de',
            type: 'date',
            value: filterValidadeDe,
            onChange: (v) => { setFilterValidadeDe(v); setPage(1); }
          },
          {
            key: 'validade_ate',
            label: 'Validade até',
            type: 'date',
            value: filterValidadeAte,
            onChange: (v) => { setFilterValidadeAte(v); setPage(1); }
          },
          {
            key: 'emissao_de',
            label: 'Emissão de',
            type: 'date',
            value: filterEmissaoDe,
            onChange: (v) => { setFilterEmissaoDe(v); setPage(1); }
          },
          {
            key: 'emissao_ate',
            label: 'Emissão até',
            type: 'date',
            value: filterEmissaoAte,
            onChange: (v) => { setFilterEmissaoAte(v); setPage(1); }
          }
        ]}
        total={total}
        page={page}
        limit={limit}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(1); }}
        searchPlaceholder="Buscar por tipo, número, órgão..."
      />

      {licencas.length === 0 ? (
        <div className="empty-state">
          <p>Nenhuma licença encontrada.</p>
          <p className="empty-state-help">Para criar uma nova licença, basta enviar o PDF. O sistema interpreta com IA os dados do empreendimento e da licença, extrai as condicionantes e interpreta cada uma com IA. Depois você define os monitoramentos na tela de detalhes.</p>
          <button className="btn btn-primary" onClick={() => setShowUploadModal(true)}>
            <FiUpload /> Enviar PDF para criar licença
          </button>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <SortableTh sortKey="empreendimento_nome" currentSort={sort} currentOrder={order} onSort={handleSort}>Empreendimento</SortableTh>
                <SortableTh sortKey="tipo" currentSort={sort} currentOrder={order} onSort={handleSort}>Tipo</SortableTh>
                <SortableTh sortKey="numero" currentSort={sort} currentOrder={order} onSort={handleSort}>Número</SortableTh>
                <SortableTh sortKey="orgao_emissor" currentSort={sort} currentOrder={order} onSort={handleSort}>Órgão Emissor</SortableTh>
                <SortableTh sortKey="data_emissao" currentSort={sort} currentOrder={order} onSort={handleSort}>Emissão</SortableTh>
                <SortableTh sortKey="data_vencimento" currentSort={sort} currentOrder={order} onSort={handleSort}>Validade</SortableTh>
                <th>Status</th>
                <th>PDF</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {licencas.map((lic) => (
                <React.Fragment key={lic.id}>
                  <tr>
                    <td>
                      {lic.empreendimento_id ? (
                        <Link to={`/empreendimentos/${lic.empreendimento_id}`} className="link-detalhes" style={{ textDecoration: 'none' }}>
                          {lic.empreendimento_nome || '-'}
                        </Link>
                      ) : (
                        (lic.empreendimento_nome || '-')
                      )}
                    </td>
                    <td>{lic.tipo || '-'}</td>
                    <td>
                      <Link to={`/licencas/${lic.id}/validar`} className="licenca-numero-link" title="Ver detalhes">
                        {lic.numero || lic.id || '-'}
                      </Link>
                    </td>
                    <td>{lic.orgao_emissor || '-'}</td>
                    <td>{formatDate(lic.data_emissao)}</td>
                    <td>{formatDate(lic.data_vencimento)}</td>
                    <td>
                      <span className={`badge ${getStatusBadge(lic.status || lic.status_automatico)}`}>
                        {getStatusLabel(lic.status || lic.status_automatico)}
                      </span>
                    </td>
                    <td>
                      {lic.arquivo_pdf ? (
                        <button
                          type="button"
                          className="pdf-link"
                          onClick={() => openLicencaPdf(lic.arquivo_pdf)}
                        >
                          📄 Ver PDF
                        </button>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-outline btn-icon"
                          onClick={() => toggleExpandLicenca(lic.id)}
                          title={expandedLicenca === lic.id ? 'Recolher condicionantes' : 'Expandir condicionantes'}
                        >
                          {expandedLicenca === lic.id ? <FiChevronDown /> : <FiChevronRight />}
                        </button>
                        <Link to={`/licencas/${lic.id}/validar`} className="btn btn-sm btn-primary btn-icon" title="Visualizar detalhes" style={{ textDecoration: 'none' }}>
                          <FiEye />
                        </Link>
                        {hasPermission('license', 'edit') && lic.arquivo_pdf && (
                          <button
                            className="btn btn-sm btn-outline btn-icon"
                            onClick={() => handleReprocessar(lic)}
                            title="Reprocessar licença com IA"
                            disabled={reprocessingId === lic.id}
                          >
                            <FiRefreshCw />
                          </button>
                        )}
                        {hasPermission('license', 'edit') && (
                          <button
                            className="btn btn-sm btn-secondary btn-icon"
                            onClick={() => handleEdit(lic)}
                            title="Editar"
                          >
                            <FiEdit2 />
                          </button>
                        )}
                        {hasPermission('license', 'archive') && (
                          <button
                            className="btn btn-sm btn-danger btn-icon"
                            onClick={() => handleDelete(lic.id)}
                            title="Excluir"
                          >
                            <FiTrash2 />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedLicenca === lic.id && condicionantes[lic.id] && (
                    <tr>
                      <td colSpan="9" className="condicionantes-container">
                        <div className="condicionantes-list">
                          <h4>Condicionantes</h4>
                          {condicionantes[lic.id].length === 0 ? (
                            <p className="text-muted">Nenhuma condicionante cadastrada</p>
                          ) : (
                            <table className="condicionantes-table">
                              <thead>
                                <tr>
                                  <th>Descrição</th>
                                  <th>Tipo</th>
                                  <th>Prazo</th>
                                  <th>Status</th>
                                  <th>Ações</th>
                                </tr>
                              </thead>
                              <tbody>
                                {condicionantes[lic.id].map((cond) => (
                                  <tr key={cond.id}>
                                    <td>{cond.descricao}</td>
                                    <td>{cond.tipo || '-'}</td>
                                    <td>{formatDate(cond.prazo)}</td>
                                    <td>
                                      <span className={`badge badge-${cond.status === 'concluida' ? 'success' : 'warning'}`}>
                                        {cond.status}
                                      </span>
                                    </td>
                                    <td>
                                      {hasPermission('condition', 'set_acl') && (
                                        <button
                                          className="btn btn-sm btn-info btn-icon"
                                          onClick={() => {
                                            setSelectedCondicionante(cond);
                                            setShowACLModal(true);
                                          }}
                                          title="Permissões"
                                        >
                                          <FiLock />
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => {
          setShowModal(false);
          setEditing(null);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editing ? <><FiEdit2 /> Editar</> : <><FiPlus /> Nova</>} Licença</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Empreendimento *</label>
                <select
                  required
                  value={formData.empreendimento_id}
                  onChange={(e) => setFormData({ ...formData, empreendimento_id: e.target.value })}
                  disabled={!!editing}
                >
                  <option value="">Selecione...</option>
                  {empreendimentos.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Tipo *</label>
                <input
                  type="text"
                  required
                  value={formData.tipo}
                  onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                  placeholder="Ex: LO, LP, LI"
                />
              </div>
              <div className="form-group">
                <label>Número</label>
                <input
                  type="text"
                  value={formData.numero}
                  onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                  placeholder="Ex: LO-2024-001"
                />
              </div>
              <div className="form-group">
                <label>Órgão Emissor</label>
                <input
                  type="text"
                  value={formData.orgao_emissor}
                  onChange={(e) => setFormData({ ...formData, orgao_emissor: e.target.value })}
                  placeholder="Ex: IMASUL, IBAMA"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Data de Emissão</label>
                  <input
                    type="date"
                    value={formData.data_emissao}
                    onChange={(e) => setFormData({ ...formData, data_emissao: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Data de Validade</label>
                  <input
                    type="date"
                    value={formData.data_vencimento}
                    onChange={(e) => setFormData({ ...formData, data_vencimento: e.target.value })}
                  />
                </div>
              </div>
              {!editing && (
                <div className="form-group">
                  <label>PDF da Licença</label>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setFormData({ ...formData, pdf: e.target.files[0] })}
                  />
                  <small className="form-help">Opcional. Apenas arquivos PDF são aceitos.</small>
                </div>
              )}
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => {
                  setShowModal(false);
                  setEditing(null);
                }}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  {editing ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showACLModal && selectedCondicionante && (
        <div className="modal-overlay" onClick={() => {
          setShowACLModal(false);
          setSelectedCondicionante(null);
        }}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <ACLManager
              entityType="condicionante"
              entityId={selectedCondicionante.id}
              onClose={() => {
                setShowACLModal(false);
                setSelectedCondicionante(null);
                if (expandedLicenca) {
                  carregarCondicionantes(expandedLicenca);
                }
              }}
            />
          </div>
        </div>
      )}

      {showUploadModal && !uploading && (
        <div className="modal-overlay" onClick={() => { setShowUploadModal(false); setUploadPdf(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2><FiUpload /> Nova licença por upload</h2>
            <p className="upload-intro">
              Basta enviar o PDF da licença. O sistema interpreta com IA os dados do empreendimento e da licença, extrai as condicionantes e interpreta cada uma com IA. Em seguida, você será levado à tela de detalhes para revisar os dados e criar os monitoramentos que geram os prazos.
            </p>
            <form onSubmit={handleUploadSubmit}>
              <div className="form-group">
                <label>Arquivo PDF *</label>
                <input
                  type="file"
                  accept=".pdf"
                  required
                  onChange={(e) => setUploadPdf(e.target.files[0] || null)}
                />
                <small className="form-help">Apenas PDF. O arquivo será processado por IA.</small>
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowUploadModal(false); setUploadPdf(null); }}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={!uploadPdf}>
                  Enviar e extrair
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {uploading && (
        <div className="upload-processing-overlay">
          <div className="upload-processing-modal">
            <div className="upload-processing-spinner" aria-hidden="true" />
            <h2 className="upload-processing-title">Extraindo dados com IA</h2>
            <p className="upload-processing-desc">
              Analisando o PDF, extraindo dados do empreendimento e da licença e interpretando condicionantes. Isso pode levar alguns minutos.
            </p>
            <div className="upload-processing-bar">
              <div className="upload-processing-bar-fill" style={{ width: `${uploadProgress}%` }} />
            </div>
            <p className="upload-processing-percent">{uploadProgress}%</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Licencas;
